// Resolves the current app URL to the label shown in the RISE Agent
// "Viewing: <section>" chip. Record *detail* pages (id in the URL) resolve to
// the record type; every other list/overview screen resolves to its section.
//
// KEEP IN SYNC with the backend SECTION_REGISTRY in
// api/src/llm/record-type-registry.ts — the chip and the agent's
// <current-screen> awareness must show the same label for the same URL. The
// parity guard is the shared URL→label table exercised by
// rise-agent-screen-labels.spec.ts (client) and
// rise-agent-screen-awareness.spec.ts (backend). See #2533.

// Specific record (detail page, id in the URL) — checked first so a Safety Case
// detail page shows "Safety Case", not the section.
const RECORD_URL_LABELS: Array<{ pattern: RegExp; label: string }> = [
    {
        pattern: /\/assurance\/register-actions\/[a-f0-9]{24}/i,
        label: "Corrective Action",
    },
    {
        pattern: /\/assurance\/registry\/[a-f0-9]{24}/i,
        label: "Safety Case",
    },
    {
        pattern: /\/assurance\/internal-audit\/[a-f0-9]{24}/i,
        label: "Internal Audit",
    },
    {
        pattern: /\/risk-management\/evaluations\/[a-f0-9]{24}/i,
        label: "Risk Evaluation",
    },
    {
        pattern: /\/risk-management\/hazard-register\/[a-f0-9]{24}/i,
        label: "Hazard",
    },
    {
        pattern: /\/risk-management\/control-library\/[a-f0-9]{24}/i,
        label: "Safety Control",
    },
    { pattern: /\/reports\/[a-f0-9]{24}\/calculate/i, label: "Report" },
    {
        pattern: /\/reports-v2\/([a-f0-9]{24}|RP-\d{5,})/i,
        label: "Report",
    },
    {
        pattern: /\/promotions\/[a-f0-9]{24}/i,
        label: "Safety Promotion",
    },
    { pattern: /\/manuals\/[a-f0-9]{24}/i, label: "Document" },
    {
        pattern: /\/policies\/objectives\/[a-f0-9]{24}/i,
        label: "Safety Objective",
    },
];

// List/overview screens — show which section the user is in. Sub-section
// patterns must come before their parent so the more specific name wins.
const SECTION_URL_LABELS: Array<{ pattern: RegExp; label: string }> = [
    {
        pattern: /\/assurance\/registry(\/|$|\?)/i,
        label: "Safety Registry",
    },
    {
        pattern: /\/assurance\/internal-audit(\/|$|\?)/i,
        label: "Internal Audits",
    },
    {
        pattern: /\/assurance\/register-actions(\/|$|\?)/i,
        label: "Action Register",
    },
    {
        pattern: /\/assurance\/advisories(\/|$|\?)/i,
        label: "Safety Advisories",
    },
    {
        pattern: /\/risk-management\/evaluations(\/|$|\?)/i,
        label: "Risk Evaluations",
    },
    {
        pattern: /\/risk-management\/hazard-register(\/|$|\?)/i,
        label: "Hazard Register",
    },
    {
        pattern: /\/risk-management\/control-library(\/|$|\?)/i,
        label: "Control Library",
    },
    // /performance before /reports so every Performance tab (/performance/reports,
    // /performance/assurance) reads "Safety Performance" rather than matching the
    // standalone reports workspace.
    { pattern: /\/performance(\/|$|\?)/i, label: "Safety Performance" },
    { pattern: /\/reports(-v2)?(\/|$|\?)/i, label: "Reports" },
    { pattern: /\/assessments(\/|$|\?)/i, label: "Reports" },
    // Sub-section labels must come before their parent (/promotions, /policies).
    {
        pattern: /\/promotions\/meeting-minutes(\/|$|\?)/i,
        label: "Meeting Minutes",
    },
    { pattern: /\/promotions(\/|$|\?)/i, label: "Safety Promotions" },
    {
        pattern: /\/policies\/objectives(\/|$|\?)/i,
        label: "Safety Objectives",
    },
    { pattern: /\/policies(\/|$|\?)/i, label: "Safety Policy" },
    { pattern: /\/manuals(\/|$|\?)/i, label: "Documents" },
    { pattern: /\/profile(\/|$|\?)/i, label: "Account Settings" },
    { pattern: /\/home(\/|$|\?)/i, label: "Home" },
];

export function resolveRiseAgentScreenLabel(url: string): string | null {
    const record = RECORD_URL_LABELS.find((r) => r.pattern.test(url))?.label;
    if (record) {
        return record;
    }
    return SECTION_URL_LABELS.find((r) => r.pattern.test(url))?.label ?? null;
}
